import React from 'react';
import { ContextMenu } from '../../../shared/context-menu/ContextMenu';
import { useSelector } from 'react-redux';
import { useTypedDispatch } from '../../../store/useTypedDispatch';
import { TagEditor } from '../../ecg/components/TagEditor';
import * as colors from '../../utils/colors';
import * as updateActions from '../../wave/actions/update.actions';
import {
  getWaveCursorSelector,
  updateSelector,
} from '../../wave/selectors/update.selectors';
import {
  selectedIdsSelector,
  selectedSelector,
  waveForIdSelector,
} from '../../wave/selectors/wave.selectors';
import { clickWave } from '../../wave/thunks/click-wave.thunk';
import { WaveId } from '../../wave/types/wave.type';
import { getUpdatedWave } from '../../wave/utils/wave.util';
import { useEventTimestamp } from '../hooks/useEventTimestamp';
import { useStrip } from '../hooks/useStrip';
import { isCtrlKey } from '../utils/mouse.utils';
import { StripSquare } from './StripSquare';
import { TagSelectionMode } from '../../wave/types/tag.type';
import { exactDurationSelector } from '../../ecg/selectors/ecg.selectors';

interface Props {
  waveId: WaveId;
  optionalTagSelectionMode?: TagSelectionMode;
}

export const Wave = ({ waveId, optionalTagSelectionMode }: Props) => {
  const dispatch = useTypedDispatch();
  const getTimestamp = useEventTimestamp();
  const baseWave = useSelector(waveForIdSelector(waveId));
  const linkedWave = useSelector(waveForIdSelector(baseWave?.link?.waveId));
  const update = useSelector(updateSelector);
  const cursor = useSelector(getWaveCursorSelector);
  const selected = useSelector(selectedSelector(waveId));
  const selectedIds = useSelector(selectedIdsSelector);
  const ecgDuration = useSelector(exactDurationSelector);
  const { onset: startingOnset } = useStrip();
  if (baseWave == null) {
    return null;
  }
  const { id, offset, onset, type } = getUpdatedWave(
    baseWave,
    update,
    ecgDuration,
    linkedWave,
  );

  const updateByBoundary = (
    event: React.MouseEvent<SVGRectElement>,
    boundary: 'onset' | 'offset',
  ) => {
    event.preventDefault();
    // event.button === 0 is main click (usually left click)
    // prevent other click (eg: right click) from moving boundary
    if (event.button !== 0) {
      return;
    }
    const onsetOffset = { onset, offset };
    dispatch(
      updateActions.setBoundary({
        id,
        boundary,
        timestamp: onsetOffset[boundary] + startingOnset,
        meta: { history: false },
      }),
    );
  };

  const startMoving = (event: React.MouseEvent<SVGRectElement>) => {
    event.preventDefault();
    // event.button === 0 is main click (usually left click)
    // prevent other click (eg: right click) from moving wave
    if (event.shiftKey || isCtrlKey(event) || event.button !== 0) {
      return;
    }

    if (!selectedIds.includes(id)) {
      toggleWave(event);
    }
    const timestamp = getTimestamp(event);
    dispatch(
      updateActions.setMoving({ id, timestamp, meta: { history: false } }),
    );
  };

  const toggleWave = (event: React.MouseEvent<SVGRectElement>) => {
    const target = event.target as SVGRectElement;
    target.focus();
    const handled = dispatch(clickWave(id, event.shiftKey, isCtrlKey(event)));
    if (handled) {
      event.preventDefault();
    }
  };

  return (
    <ContextMenu<HTMLDivElement>
      renderMenu={({ onClose: closeContextMenu }) => (
        <TagEditor
          waveId={waveId}
          onClose={closeContextMenu}
          optionalTagSelectionMode={optionalTagSelectionMode}
        />
      )}
      menuProps={{
        closeOnSelect: false,
        placement: 'right-start',
      }}
    >
      {(ref) => (
        <StripSquare
          ref={ref}
          data-testid='wave'
          onset={onset + startingOnset}
          offset={offset + startingOnset}
          cursor={cursor}
          main={{
            style: {
              fill: colors.typeToColor[type],
              fillOpacity: selected
                ? colors.manualWaveOpacity
                : colors.waveOpacity,
            },
            onMouseDown: startMoving,
            onClick: toggleWave,
          }}
          leftHandle={{
            onMouseDown: (event) => updateByBoundary(event, 'onset'),
            onClick: toggleWave,
          }}
          rightHandle={{
            onMouseDown: (event) => updateByBoundary(event, 'offset'),
            onClick: toggleWave,
          }}
        />
      )}
    </ContextMenu>
  );
};
